/**
 * One account's figures on the Overview (A8): the last 7 full days and the same with
 * today so far, three ratios with their formulas, a bar per day, the campaigns with a way
 * into each one's card, and X's signals beside them. Everything comes from
 * `GET /api/v1/overview-stats` (and the signal lines already read for the account card) —
 * no call to X from here.
 *
 * The decisions are in `lib/overview-stats.ts`, the words in `lib/overview-stats-copy.ts`;
 * this file renders them and decides nothing. The bars are plain boxes, no chart library.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { OverviewFigures, OverviewStatsAnswer, XAccount } from "../lib/api.ts";
import {
	BARS_HEADING,
	BAR_METRIC_LABEL,
	CAMPAIGNS_HEADING,
	CAMPAIGN_COLUMN,
	DAYS_COLUMN,
	ESTIMATE_NOTE,
	FIGURE_NAMES,
	NARROW_COLUMNS,
	NOT_READ_YET_BANNER,
	NO_CAMPAIGNS,
	NO_SERVING,
	OPEN_CARD,
	PERIOD_FULL,
	PERIOD_WITH_TODAY,
	PLACEMENT_NOTE,
	RATIOS,
	READ_FAILED,
	SIGNALS_HEADING,
	SUM_NOTE,
	TODAY_NOTE,
	barTitle,
	campaignNameText,
	cardHref,
	costText,
	cycleUnfinishedText,
	dayNotFinalText,
	dayPartialText,
	figureText,
	partialText,
	percentText,
	ratioHint,
	readText,
	sectionTitle,
	unavailableText,
} from "../lib/overview-stats-copy.ts";
import {
	type BarMetric,
	type NamesRead,
	type OverviewStatsState,
	bars,
	campaignName,
	coverage,
	cpcMicro,
	cpmMicro,
	ctr,
	cycleUnfinished,
	dayPartial,
	noServing,
} from "../lib/overview-stats.ts";
import { Card, InfoBanner, Select, Table } from "../ui/index.ts";

const METRICS: readonly BarMetric[] = ["billedMicro", "impressions", "engagements", "urlClicks"];
/** The campaigns table's columns that go when it is narrower than 52rem (`NARROW_COLUMNS` says where). */
const NARROW_HIDDEN: readonly BarMetric[] = ["engagements", "urlClicks"];

interface Props {
	account: XAccount;
	state: OverviewStatsState;
	/** The account's currency, from its funding instruments as the card read them — or null. */
	currency: string | null;
	/** The account's campaign list, read for names only. */
	names: NamesRead;
	/** The signal lines the account card already shows, rendered beside the figures. */
	signals: { key: string; text: string }[];
}

function Totals({ f, currency }: { f: OverviewFigures; currency: string | null }) {
	return (
		<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
			{METRICS.map((m) => (
				<div key={m} className="contents">
					<dt className="text-muted">{FIGURE_NAMES[m]}</dt>
					<dd className={f[m] === null ? "text-xs text-muted" : "text-content"}>
						{figureText(m, f[m], currency)}
					</dd>
				</div>
			))}
			<dt className="text-muted" title={ratioHint(RATIOS.ctr.formula)}>
				{RATIOS.ctr.name}
			</dt>
			<dd className="text-content" title={ratioHint(RATIOS.ctr.formula)}>
				{percentText(ctr(f))}
			</dd>
			<dt className="text-muted" title={ratioHint(RATIOS.cpm.formula)}>
				{RATIOS.cpm.name}
			</dt>
			<dd className="text-content" title={ratioHint(RATIOS.cpm.formula)}>
				{costText(cpmMicro(f), currency)}
			</dd>
			<dt className="text-muted" title={ratioHint(RATIOS.cpc.formula)}>
				{RATIOS.cpc.name}
			</dt>
			<dd className="text-content" title={ratioHint(RATIOS.cpc.formula)}>
				{costText(cpcMicro(f), currency)}
			</dd>
		</dl>
	);
}

function Figures({
	account,
	answer,
	currency,
	names,
}: {
	account: XAccount;
	answer: OverviewStatsAnswer;
	currency: string | null;
	names: NamesRead;
}) {
	const [metric, setMetric] = useState<BarMetric>("billedMicro");
	const covered = coverage(answer);
	const unfinished = cycleUnfinished(answer.cycle);
	return (
		<>
			<p className="text-xs text-muted">{readText(answer.readAt, answer.stale)}</p>
			{unfinished && <p className="text-xs text-warning">{cycleUnfinishedText(unfinished)}</p>}
			{covered?.partial && (
				<p className="text-xs text-warning">{partialText(covered.read, covered.listed)}</p>
			)}

			<div className="mt-3 grid gap-6 sm:grid-cols-2">
				<div>
					<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">
						{PERIOD_FULL}
					</h4>
					<Totals f={answer.total} currency={currency} />
				</div>
				<div>
					<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted">
						{PERIOD_WITH_TODAY}
					</h4>
					<Totals f={answer.totalWithToday} currency={currency} />
					<p className="mt-1 text-xs text-muted">{TODAY_NOTE}</p>
				</div>
			</div>
			<ul className="mt-3 list-none space-y-0.5 p-0 text-xs text-muted">
				<li>{PLACEMENT_NOTE}</li>
				<li>{SUM_NOTE}</li>
				<li>{ESTIMATE_NOTE}</li>
			</ul>

			<h4 className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted">
				{BARS_HEADING}
			</h4>
			<div className="mt-1 w-56">
				<Select
					label={BAR_METRIC_LABEL}
					value={metric}
					onChange={(e) => setMetric(e.target.value as BarMetric)}
					options={METRICS.map((m) => ({ value: m, label: FIGURE_NAMES[m] }))}
				/>
			</div>
			<div className="mt-2 flex h-32 items-end gap-2">
				{bars(answer.days, metric).map((b, i) => {
					const day = answer.days[i];
					const text = figureText(metric, b.value, currency);
					return (
						<div key={b.day} className="flex h-full flex-1 flex-col items-center justify-end">
							<div
								className={b.height === null ? "w-full" : "w-full bg-primary"}
								style={{ height: b.height === null ? 0 : `${b.height}%` }}
								title={barTitle(b.day, text)}
							/>
							<span className="mt-1 text-[10px] text-muted" title={barTitle(b.day, text)}>
								{b.day.slice(5)}
							</span>
							{dayPartial(day, answer.campaignsListed) && answer.campaignsListed !== null && (
								<span className="text-[10px] text-warning">
									{dayPartialText(day.campaigns, answer.campaignsListed)}
								</span>
							)}
							{day.partialCampaigns > 0 && (
								<span className="text-[10px] text-warning">
									{dayNotFinalText(day.partialCampaigns, day.campaigns)}
								</span>
							)}
						</div>
					);
				})}
			</div>

			<h4 className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted">
				{CAMPAIGNS_HEADING}
			</h4>
			{/*
			 * 🔴 The table fits its panel (3d, 30.10: a sideways bar at 1280 on prod). Dense cells,
			 * the words wrap, and below 52rem of its own width Engagements and URL clicks go —
			 * with a line that says where they are read, so nothing goes without a word.
			 */}
			<div className="@container">
				<Table
					dense
					columns={[
						{
							id: "id",
							header: CAMPAIGN_COLUMN,
							wrap: true,
							accessor: (c) => (
								<span className="flex flex-col">
									<Link
										to={cardHref(account.id, c.id)}
										className="font-mono text-xs text-primary underline"
									>
										{c.id} · {OPEN_CARD}
									</Link>
									<span className="text-xs text-muted">
										{campaignNameText(campaignName(c.id, names))}
									</span>
								</span>
							),
						},
						...METRICS.map((m) => ({
							id: m,
							header: FIGURE_NAMES[m],
							className: NARROW_HIDDEN.includes(m) ? "hidden @min-[52rem]:table-cell" : undefined,
							accessor: (c: (typeof answer.campaigns)[number]) => (
								<span className="text-xs">{figureText(m, c[m], currency)}</span>
							),
						})),
						{
							id: "ctr",
							header: RATIOS.ctr.name,
							accessor: (c) => (
								<span className="text-xs" title={ratioHint(RATIOS.ctr.formula)}>
									{percentText(ctr(c))}
								</span>
							),
						},
						{
							id: "days",
							header: DAYS_COLUMN,
							wrap: true,
							accessor: (c) => (
								<span className="text-xs text-muted">
									{String(c.days)}
									{noServing(c) && ` · ${NO_SERVING}`}
								</span>
							),
						},
					]}
					data={answer.campaigns}
					keyExtractor={(c) => c.id}
					emptyMessage={NO_CAMPAIGNS}
				/>
				{answer.campaigns.length > 0 && (
					<p className="mt-1 text-xs text-muted @min-[52rem]:hidden">{NARROW_COLUMNS}</p>
				)}
			</div>
		</>
	);
}

export function OverviewAnalytics({ account, state, currency, names, signals }: Props) {
	return (
		<Card className="@container p-4">
			<h3 className="font-semibold text-content">{sectionTitle(account.name)}</h3>
			{/* X's signals beside the figures only when the card has the room; below them otherwise. */}
			<div className="mt-3 grid gap-6 @5xl:grid-cols-[1fr_16rem]">
				<div className="min-w-0">
					{state.kind === "loading" && <p className="text-sm text-muted">…</p>}
					{state.kind === "failed" && (
						<InfoBanner variant="danger" title={READ_FAILED}>
							{state.why}
						</InfoBanner>
					)}
					{state.kind === "unavailable" && (
						<InfoBanner variant="warning" title={unavailableText(state.why).title}>
							{unavailableText(state.why).body}
						</InfoBanner>
					)}
					{state.kind === "not-read-yet" && (
						<>
							<InfoBanner variant="info" title={NOT_READ_YET_BANNER.title}>
								{NOT_READ_YET_BANNER.body}
							</InfoBanner>
							<p className="mt-2 text-xs text-muted">
								{readText(state.answer.readAt, state.answer.stale)}
							</p>
						</>
					)}
					{state.kind === "ready" && (
						<Figures account={account} answer={state.answer} currency={currency} names={names} />
					)}
				</div>
				<aside>
					<h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
						{SIGNALS_HEADING}
					</h4>
					<ul className="mt-1 list-none p-0 text-xs text-muted">
						{signals.map((l) => (
							<li key={l.key}>{l.text}</li>
						))}
					</ul>
				</aside>
			</div>
		</Card>
	);
}
